<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import Translator from "@/utils/common/Translator.ts";
import type {
  BIDrillDownTarget,
  BIDrillDownByDimension,
  BIDrillDownPointValues,
  BIDrillDownFilterMetadata,
  BILinkTarget,
  BILinkPointValues,
} from "@/components/bi/biChartTypes.ts";

interface Props {
  modelValue: boolean;
  targets?: BIDrillDownTarget[];
  pointValues?: BIDrillDownPointValues;
  filterMetadata?: Record<string, BIDrillDownFilterMetadata>;
  drillDownByDimensions?: BIDrillDownByDimension[];
  linkTargets?: BILinkTarget[];
  linkPointValues?: BILinkPointValues;
  sourceLabel?: string;
  x?: number;
  y?: number;
}

const props = withDefaults(defineProps<Props>(), {
  targets: () => [],
  pointValues: () => ({}),
  filterMetadata: () => ({}),
  drillDownByDimensions: () => [],
  linkTargets: () => [],
  linkPointValues: () => ({}),
  x: 0,
  y: 0,
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'drillDownSelected': [target: BIDrillDownTarget];
  'drillDownBySelected': [dim: BIDrillDownByDimension];
  'linkSelected': [link: BILinkTarget];
}>();

const anchorRef = ref<HTMLElement>();

const menuOpen = computed<boolean>({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

function drillDownLabel(target: BIDrillDownTarget): string {
  const isArabic = Translator.isArabic();
  const primary = isArabic ? target.arTitle : target.enTitle;
  const fallback = isArabic ? target.enTitle : target.arTitle;
  return primary || fallback || Translator.translate("biDrillDown");
}

function drillDownByLabel(dimension: BIDrillDownByDimension): string {
  const isArabic = Translator.isArabic();
  const primary = isArabic ? dimension.arTitle : dimension.enTitle;
  const fallback = isArabic ? dimension.enTitle : dimension.arTitle;
  return primary || fallback || dimension.fieldId;
}

function linkLabel(linkTarget: BILinkTarget): string {
  const isArabic = Translator.isArabic();
  const base = isArabic ? linkTarget.arLabel : linkTarget.enLabel;
  const fallback = isArabic ? linkTarget.enLabel : linkTarget.arLabel;
  const label = base || fallback || linkTarget.key;
  const pv = props.linkPointValues[linkTarget.key];
  if (pv?.label) return `${label} "${pv.label}"`;
  return label;
}

watch(() => props.modelValue, (v) => {
  if (!v) return;
});
</script>

<template>
  <div ref="anchorRef"
       class="bi-action-menu-anchor"
       :style="{left: x + 'px', top: y + 'px'}"/>
  <q-menu v-model="menuOpen"
          :target="anchorRef"
          :no-parent-event="true"
          anchor="bottom right"
          self="top start">
    <q-list dense style="min-width: 180px">
      <q-item v-if="sourceLabel" dense class="tw-text-xs tw-text-gray-500">
        <q-item-section>{{ sourceLabel }}</q-item-section>
      </q-item>
      <q-separator v-if="sourceLabel"/>
      <template v-if="linkTargets?.length">
        <q-item-label header class="tw-text-xs">
          {{ Translator.translate('biNavigateTo') }}
        </q-item-label>
        <q-item v-for="link in linkTargets"
                :key="link.key"
                clickable v-close-popup
                @click="emit('linkSelected', link)">
          <q-item-section avatar><q-icon :name="link.isDirectLink ? 'open_in_new' : 'launch'"/></q-item-section>
          <q-item-section>{{ linkLabel(link) }}</q-item-section>
        </q-item>
      </template>
      <template v-if="targets?.length">
        <q-separator v-if="linkTargets?.length"/>
        <q-item-label v-if="drillDownByDimensions?.length || linkTargets?.length" header class="tw-text-xs">
          {{ Translator.translate('biOpenIn') }}
        </q-item-label>
        <q-item v-for="target in targets"
                :key="target.key || target.targetWidgetId || target.targetDashboardId"
                clickable v-close-popup
                @click="emit('drillDownSelected', target)">
          <q-item-section avatar><q-icon :name="target.targetType === 'dashboard' ? 'dashboard' : 'arrow_forward'"/></q-item-section>
          <q-item-section>{{ drillDownLabel(target) }}</q-item-section>
        </q-item>
      </template>
      <template v-if="drillDownByDimensions?.length">
        <q-separator v-if="targets?.length || linkTargets?.length"/>
        <q-item-label header class="tw-text-xs">
          {{ Translator.translate('biDrillDownBy') }}
        </q-item-label>
        <q-item v-for="dim in drillDownByDimensions"
                :key="dim.fieldId"
                clickable v-close-popup
                @click="emit('drillDownBySelected', dim)">
          <q-item-section avatar><q-icon name="unfold_more"/></q-item-section>
          <q-item-section>{{ drillDownByLabel(dim) }}</q-item-section>
        </q-item>
      </template>
    </q-list>
  </q-menu>
</template>

<style scoped>
.bi-action-menu-anchor {
  position: fixed;
  width: 1px;
  height: 1px;
  pointer-events: none;
}
</style>
